import React, {useEffect, useMemo, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {ChannelLogo} from '../../channels/ChannelLogo';
import {
    ANALYTICS_CHANNEL, DV360_CHANNEL, FACEBOOK_CHANNEL, GOOGLE_CHANNEL, KLAVIYO_CHANNEL,
    LINKEDIN_CHANNEL, SEARCH_CONSOLE_CHANNEL, SHEET_CHANNEL, SHOPIFY_CHANNEL, TIENDA_NUBE_CHANNEL,
    TIKTOK_CHANNEL, VTEX_CHANNEL, WOOCOMMERCE_CHANNEL,
} from '../../channels/channels';

export const SOURCE_AD_ACCOUNTS = 'AD_ACCOUNTS';
export const SOURCE_BUSINESS_UNITS = 'BUSINESS_UNITS';

/** Desde cuántas opciones el desplegable muestra el buscador. */
export const SEARCH_FROM = 10;

/** El orden de la lista: primero el paid media, después analytics y ecommerce. */
const CHANNEL_ORDER = [
    GOOGLE_CHANNEL, FACEBOOK_CHANNEL, TIKTOK_CHANNEL, LINKEDIN_CHANNEL, DV360_CHANNEL,
    ANALYTICS_CHANNEL, SEARCH_CONSOLE_CHANNEL,
    SHOPIFY_CHANNEL, TIENDA_NUBE_CHANNEL, WOOCOMMERCE_CHANNEL, VTEX_CHANNEL, KLAVIYO_CHANNEL,
    SHEET_CHANNEL,
];

/** El identificador de una cuenta en `selected_items`: `"{channel}__{account_id}"` (contrato del Explorador). */
export function accountItemId(channel, accountId) {
    return `${channel}__${accountId}`;
}

function byName(a, b) {
    return String(a.name || '').localeCompare(String(b.name || ''), undefined, {sensitivity: 'base'});
}

/**
 * Ordena las cuentas por canal (el orden fijo de arriba) y, dentro de cada canal, por nombre.
 * El backend las devuelve en el orden en que se conectaron: mezcladas, la lista se lee como un
 * revoltijo de logos y hay que barrerla entera para encontrar una.
 */
export function sortAdAccounts(accounts) {
    const rank = (channel) => {
        const i = CHANNEL_ORDER.indexOf(channel);
        return i === -1 ? CHANNEL_ORDER.length : i;
    };
    return [...accounts].sort((a, b) => rank(a.channel) - rank(b.channel) || byName(a, b));
}

/** Compara sin acentos ni mayúsculas: «Praga» encuentra a «Prágá» y al revés. */
function fold(text) {
    return String(text || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
}

/** Una opción entra en la búsqueda por su nombre o por su id (la agencia busca por las dos cosas). */
export function matchesText(query, ...fields) {
    const needle = fold(query).trim();
    if (!needle) return true;
    return fields.some((field) => fold(field).includes(needle));
}

/**
 * El multi-selector de cuentas publicitarias / líneas de control de los Ajustes. "Incluir todas"
 * = selección vacía.
 *
 * Props: sourceType, agencyClientId, businessUnits, adAccounts, selectedItems, onChange(items),
 *        onSourceTypeChange(sourceType), flash (bool: resaltar tras un cambio de cliente)
 */
export function AccountsScopeSelector({
    sourceType, agencyClientId, businessUnits = [], adAccounts = [], selectedItems = [],
    onChange, onSourceTypeChange, flash = false,
}) {
    const {t} = useTranslation();
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState('');
    const wrapperRef = useRef(null);
    const buttonRef = useRef(null);

    // Al cerrar se olvida la búsqueda: la próxima apertura arranca mostrando la lista entera.
    useEffect(() => {
        if (!open) setQuery('');
    }, [open]);

    useEffect(() => {
        if (!open) return undefined;
        const onClickOutside = (e) => {
            if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setOpen(false);
        };
        const onKeyDown = (e) => {
            if (e.key !== 'Escape') return;
            setOpen(false);
            if (buttonRef.current) buttonRef.current.focus();
        };
        document.addEventListener('mousedown', onClickOutside);
        document.addEventListener('keydown', onKeyDown);
        return () => {
            document.removeEventListener('mousedown', onClickOutside);
            document.removeEventListener('keydown', onKeyDown);
        };
    }, [open]);

    const clientKey = agencyClientId == null ? null : String(agencyClientId);
    const isBusinessUnits = sourceType === SOURCE_BUSINESS_UNITS;

    // `eventOrId`: las filas de acá entregan el id, pero un checkbox entrega el evento si vuelve a
    // enchufarse un componente de lista compartido. Así llegaba `MultiSelectAdAccountItem`, que
    // pasa el evento del DOM: `String(evento)` guardaba `"[object Object]"` en `selected_items`, la
    // cuenta nunca quedaba tildada y el clic siguiente —en cualquier otra— borraba esa basura.
    const toggle = (eventOrId) => {
        const id = eventOrId && typeof eventOrId === 'object' && eventOrId.target
            ? eventOrId.target.dataset.id
            : String(eventOrId);
        if (!id) return;
        onChange(selectedItems.includes(id) ? selectedItems.filter((i) => i !== id) : [...selectedItems, id]);
    };

    const options = useMemo(() => {
        if (!clientKey) return [];
        if (isBusinessUnits) {
            return businessUnits
                .filter((bu) => String(bu.agency_client_id) === clientKey)
                .sort(byName)
                .map((bu) => ({id: String(bu.id), name: bu.name, hint: null, channel: null}));
        }
        return sortAdAccounts(adAccounts.filter((aa) => String(aa.agency_client_id) === clientKey))
            .map((aa) => ({
                id: accountItemId(aa.channel, aa.account_id),
                name: aa.name,
                hint: String(aa.account_id),
                channel: aa.channel,
            }));
    }, [isBusinessUnits, businessUnits, adAccounts, clientKey]);

    const visible = useMemo(
        () => options.filter((o) => matchesText(query, o.name, o.hint)),
        [options, query],
    );

    const allLabel = isBusinessUnits
        ? t('informes.settings.all_business_units', 'Incluir todas las líneas de seguimiento')
        : t('informes.settings.all_ad_accounts', 'Incluir todas las cuentas');
    const selectedCountLabel = isBusinessUnits
        ? t('informes.settings.lines_selected', {count: selectedItems.length, defaultValue: '{{count}} líneas seleccionadas'})
        : t('informes.settings.accounts_selected', {count: selectedItems.length, defaultValue: '{{count}} cuentas seleccionadas'});
    const buttonText = selectedItems.length === 0 ? allLabel : selectedCountLabel;
    const showSearch = options.length >= SEARCH_FROM;

    return (
        <div className={`inf-scope ${flash ? '-flash' : ''}`} ref={wrapperRef}>
            <select className="inf-scope-source" value={sourceType || SOURCE_AD_ACCOUNTS}
                    onChange={(e) => onSourceTypeChange(e.target.value)}>
                <option value={SOURCE_AD_ACCOUNTS}>{t('data_explorer.ad_accounts', 'Cuentas publicitarias')}</option>
                <option value={SOURCE_BUSINESS_UNITS}>{t('data_explorer.business_units', 'Líneas de seguimiento (y sus campañas)')}</option>
            </select>
            <div className="inf-scope-picker">
                <button type="button" ref={buttonRef} className={`inf-scope-button ${open ? '-open' : ''}`}
                        onClick={() => setOpen(!open)} aria-haspopup="true" aria-expanded={open}>
                    {buttonText}
                </button>
                {open && (
                    <div className="inf-scope-dropdown" data-testid="inf-scope-dropdown">
                        {showSearch && (
                            <div className="inf-scope-search">
                                <input type="text" value={query} autoFocus
                                       onChange={(e) => setQuery(e.target.value)}
                                       placeholder={t('multiselect.search_placeholder', 'Buscar...')}/>
                            </div>
                        )}
                        <div className="inf-scope-head">
                            <label className={`inf-scope-opt -all ${selectedItems.length === 0 ? '-on' : ''}`}>
                                <input type="checkbox" checked={selectedItems.length === 0}
                                       onChange={() => onChange([])}/>
                                <span className="inf-scope-opt-text">
                                    <span className="inf-scope-opt-name">{allLabel}</span>
                                </span>
                            </label>
                        </div>
                        {visible.length > 0 ? (
                            <ul className="inf-scope-list">
                                {visible.map((option) => {
                                    const checked = selectedItems.includes(option.id);
                                    return (
                                        <li key={option.id}>
                                            <label className={`inf-scope-opt ${checked ? '-on' : ''}`}
                                                   data-testid="inf-scope-opt">
                                                <input type="checkbox" checked={checked} data-id={option.id}
                                                       onChange={() => toggle(option.id)}/>
                                                {option.channel && (
                                                    <span className="inf-scope-logo">
                                                        <ChannelLogo useOld={false} channel={option.channel}
                                                                     marginLeft={0} padding={0} width={18} height={18}
                                                                     hspace={0} vspace={0}/>
                                                    </span>
                                                )}
                                                <span className="inf-scope-opt-text">
                                                    <span className="inf-scope-opt-name" title={option.name}
                                                          data-testid="inf-scope-opt-name">{option.name}</span>
                                                    {option.hint && <span className="inf-scope-opt-id"
                                                                          data-testid="inf-scope-opt-id">{option.hint}</span>}
                                                </span>
                                            </label>
                                        </li>
                                    );
                                })}
                            </ul>
                        ) : (
                            <p className="inf-scope-empty">
                                {options.length === 0
                                    ? t('multiselect.no_matches', 'No hay elementos para mostrar.')
                                    : t('multiselect.no_matches_search', 'Ningún resultado para tu búsqueda.')}
                            </p>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}
